import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getSignalConfigStorageKey,
  getLegacySignalConfigStorageKey,
  indexSignalConfigs,
  loadLocalSignalConfigs,
  normalizeSignalConfigs,
  saveLocalSignalConfigs,
} from "./localSignals";
import {
  getSharedDashboardLayout,
  saveSharedSignalConfigs,
} from "@/features/machines/layoutApi";
import type { SignalConfig } from "./types";
import { confirmLayoutResponse, type LayoutConfirmation } from "@/features/machines/layoutConfirmation";
import { jsonStructurallyEqual } from "@/lib/jsonEquality";

interface UseSignalConfigsParams {
  enabled?: boolean;
  workspaceId: string;
  machineId: string;
  /**
   * Versão da config física (configHash). Versiona o storage junto com os
   * widgets (P0.5) — overrides do operador não sobrevivem a uma mudança de
   * configuração para não grudarem num sinal que mudou de tipo.
   */
  configVersion?: string | null;
  /** Escopo do operador atual. Enquanto nao ha login real, usa `operator-local`. */
  userId?: string | null;
}

export interface UseSignalConfigsResult {
  /** Confirmacao de leitura remota, nunca inferida de cache ou array nao vazio. */
  remoteConfirmation: LayoutConfirmation | null;
  signalConfigs: SignalConfig[];
  /** Mapa source -> config, pronto para lookup O(1). */
  index: Map<string, SignalConfig>;
  /** Insere ou substitui o config dessa `source`. Persiste imediatamente. */
  upsert: (config: SignalConfig) => void;
  /** Remove o config dessa `source`. Persiste imediatamente. */
  remove: (source: string) => void;
}

/**
 * Estado compartilhado de `SignalConfig` (`ghm:signal-configs:{ws}:{m}`).
 * Centralizar aqui evita que `MachineDashboardPage` e `WidgetPanel` leiam
 * o storage independentemente e desincronizem quando o editor de widget
 * atualizar a config.
 */
export function useSignalConfigs({
  enabled = true,
  workspaceId,
  machineId,
  configVersion,
  userId,
}: UseSignalConfigsParams): UseSignalConfigsResult {
  const storageKey = useMemo(
    () => getSignalConfigStorageKey(workspaceId, machineId, configVersion, userId),
    [workspaceId, machineId, configVersion, userId],
  );
  const legacyStorageKey = useMemo(
    () => getLegacySignalConfigStorageKey(workspaceId, machineId, configVersion),
    [workspaceId, machineId, configVersion],
  );
  const legacyUnversionedStorageKey = useMemo(
    () => getLegacySignalConfigStorageKey(workspaceId, machineId),
    [workspaceId, machineId],
  );
  const [signalConfigs, setSignalConfigs] = useState<SignalConfig[]>([]);
  const [confirmation, setConfirmation] = useState<{
    scope: string; value: LayoutConfirmation | null;
  } | null>(null);
  const localRevision = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined" || !enabled) return;
    const controller = new AbortController();
    let cancelled = false;
    const readRevision = localRevision.current;
    setConfirmation(null);

    const localConfigs = loadConfigsFromLocalStorage({
      storage: window.localStorage,
      machineId,
      storageKey,
      legacyStorageKey,
      legacyUnversionedStorageKey,
    });
    setSignalConfigs(localConfigs);

    getSharedDashboardLayout(
      workspaceId,
      machineId,
      configVersion,
      controller.signal,
    )
      .then((layout) => {
        // Nao reaplicar nem migrar a resposta de um GET anterior a uma edicao:
        // isso apagaria a escolha atual do operador no cache e no backend.
        if (cancelled || readRevision !== localRevision.current) return;
        const confirmed = confirmLayoutResponse(layout, workspaceId, machineId, configVersion);
        if (layout.exists) {
          const remoteConfigs = normalizeSignalConfigs(layout.signalConfigs, machineId);
          const normalizationChanged = !jsonStructurallyEqual(remoteConfigs, layout.signalConfigs);
          saveLocalSignalConfigs(window.localStorage, storageKey, remoteConfigs);
          setSignalConfigs(remoteConfigs);
          if (!normalizationChanged) {
            setConfirmation({ scope: storageKey, value: confirmed });
          }
          if (normalizationChanged) {
            void saveSharedSignalConfigs(
              workspaceId,
              machineId,
              configVersion,
              remoteConfigs,
            ).catch((error) => {
              console.warn("Falha ao salvar configs compartilhadas normalizadas.", error);
            });
          }
          return;
        }

        if (localConfigs.length > 0) {
          void saveSharedSignalConfigs(
            workspaceId,
            machineId,
            configVersion,
            localConfigs,
          ).catch((error) => {
            console.warn(
              "Falha ao migrar configs locais para layout compartilhado.",
              error,
            );
          });
        } else {
          setConfirmation({ scope: storageKey, value: confirmed });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        console.warn("Falha ao carregar configs compartilhadas; usando cache local.", error);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    enabled,
    configVersion,
    legacyStorageKey,
    legacyUnversionedStorageKey,
    machineId,
    storageKey,
    workspaceId,
  ]);

  const persist = useCallback(
    (next: SignalConfig[]) => {
      setConfirmation(null);
      localRevision.current++;
      const normalized = normalizeSignalConfigs(next, machineId);
      setSignalConfigs(normalized);
      if (typeof window !== "undefined") {
        saveLocalSignalConfigs(window.localStorage, storageKey, normalized);
      }
      void saveSharedSignalConfigs(workspaceId, machineId, configVersion, normalized).catch(
        (error) => {
          console.warn("Falha ao salvar configs no layout compartilhado.", error);
        },
      );
    },
    [configVersion, machineId, storageKey, workspaceId],
  );

  const upsert = useCallback(
    (config: SignalConfig) => {
      const without = signalConfigs.filter((c) => c.source !== config.source);
      persist([...without, config].sort((a, b) => a.source.localeCompare(b.source)));
    },
    [persist, signalConfigs],
  );

  const remove = useCallback(
    (source: string) => {
      persist(signalConfigs.filter((c) => c.source !== source));
    },
    [persist, signalConfigs],
  );

  const index = useMemo(() => indexSignalConfigs(signalConfigs), [signalConfigs]);

  return { signalConfigs, index, upsert, remove,
    remoteConfirmation: enabled && confirmation?.scope === storageKey ? confirmation.value : null };
}

function loadConfigsFromLocalStorage({
  storage,
  machineId,
  storageKey,
  legacyStorageKey,
  legacyUnversionedStorageKey,
}: {
  storage: Storage;
  machineId: string;
  storageKey: string;
  legacyStorageKey: string;
  legacyUnversionedStorageKey: string;
}): SignalConfig[] {
  if (storage.getItem(storageKey) !== null) {
    return loadLocalSignalConfigs(storage, storageKey, machineId);
  }

  let legacyConfigs = loadLocalSignalConfigs(storage, legacyStorageKey, machineId);
  if (legacyConfigs.length === 0 && legacyUnversionedStorageKey !== legacyStorageKey) {
    legacyConfigs = loadLocalSignalConfigs(storage, legacyUnversionedStorageKey, machineId);
  }
  if (legacyConfigs.length > 0) {
    saveLocalSignalConfigs(storage, storageKey, legacyConfigs);
  }
  return legacyConfigs;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
